<script setup>
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import EventSidebarSection from './EventSidebarSection.vue';
import DropdownSearch from 'dashboard/components-next/dropdown-search/DropdownSearch.vue';
import { useRoute, useRouter } from 'vue-router';

const props = defineProps({
  customer: { type: Object, default: null },
  formattedContacts: { type: Array, default: () => [] },
  selectedCustomer: { type: Object, default: () => ({}) },
  showCustomerDropdown: { type: Boolean, default: false },
  searchingContacts: { type: Boolean, default: false },
  readOnly: { type: Boolean, default: false },
});

const emit = defineEmits([
  'updateCustomer',
  'openCustomerDropdown',
  'closeCustomerDropdown',
  'searchCustomer',
]);

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const openContactDetails = () => {
  if (!props.customer?.id) return;
  router.push({
    name: 'contacts_edit',
    params: {
      accountId: route.params.accountId,
      contactId: props.customer.id,
    },
  });
};
</script>

<template>
  <EventSidebarSection
    :title="t('PLANNING.SHOW.CONTACT_INFO')"
    icon="i-lucide-handshake"
  >
    <div class="flex flex-col gap-4">
      <div
        v-if="customer"
        class="group/customer relative flex h-10 items-center gap-3"
      >
        <DropdownSearch
          v-if="showCustomerDropdown"
          :options="formattedContacts"
          :selected-item="selectedCustomer"
          has-thumbnail
          :is-searching="searchingContacts"
          :title="t('PLANNING.EVENTS.SELECT_CUSTOMER')"
          :no-search-result="t('PLANNING.SHOW.NO_CONTACTS_FOUND')"
          :input-placeholder="t('PLANNING.SHOW.SEARCH_CONTACTS')"
          @select="emit('updateCustomer', $event)"
          @close="emit('closeCustomerDropdown')"
          @search="emit('searchCustomer', $event)"
        />
        <div
          class="flex min-w-0 flex-1 cursor-pointer items-center gap-3"
          @click="openContactDetails"
        >
          <Avatar
            :src="customer.thumbnail"
            :name="customer.name || t('PLANNING.SHOW.UNKNOWN_CONTACT')"
            :size="40"
            rounded-full
          />
          <div class="min-w-0 flex-1">
            <p
              class="m-0 truncate text-sm font-semibold leading-5 text-n-slate-12"
            >
              {{ customer.name || t('PLANNING.SHOW.UNKNOWN_CONTACT') }}
            </p>
            <p class="m-0 truncate text-xs leading-4 text-black">
              {{ customer.email }}
            </p>
          </div>
        </div>
        <span
          class="shrink-0 rounded-full bg-woot-50 px-2.5 py-0.5 text-xs font-medium text-woot-600 dark:bg-woot-900/20 dark:text-woot-300"
        >
          {{ t('PLANNING.SHOW.CUSTOMER') }}
        </span>
        <button
          v-if="!readOnly"
          class="absolute left-1.5 z-10 hidden h-7 w-7 items-center justify-center rounded-full bg-woot-500 hover:bg-woot-300 text-white group-hover/customer:inline-flex print:hidden"
          :title="t('PLANNING.SHOW.CHANGE_CONTACT')"
          @click.stop="emit('openCustomerDropdown')"
        >
          <Icon icon="i-lucide-pen-line" class="size-4 shrink-0" />
        </button>
      </div>
      <div
        v-else-if="!customer && !readOnly"
        class="relative flex h-10 cursor-pointer items-center gap-3 rounded-lg hover:bg-n-alpha-2 print:hidden"
        @click.stop="emit('openCustomerDropdown')"
      >
        <DropdownSearch
          v-if="showCustomerDropdown"
          :options="formattedContacts"
          :selected-item="selectedCustomer"
          has-thumbnail
          :is-searching="searchingContacts"
          :title="t('PLANNING.EVENTS.SELECT_CUSTOMER')"
          :no-search-result="t('PLANNING.SHOW.NO_CONTACTS_FOUND')"
          :input-placeholder="t('PLANNING.SHOW.SEARCH_CONTACTS')"
          @select="emit('updateCustomer', $event)"
          @close="emit('closeCustomerDropdown')"
          @search="emit('searchCustomer', $event)"
        />
        <div
          class="flex size-10 shrink-0 items-center justify-center rounded-full bg-n-alpha-2"
        >
          <Icon icon="i-lucide-user-plus" class="size-4 text-n-slate-10" />
        </div>
        <p class="m-0 text-sm leading-5 text-black">
          {{ t('PLANNING.SHOW.ASSIGN_CUSTOMER') }}
        </p>
      </div>
      <div v-else-if="!customer" class="flex h-10 items-center gap-3">
        <p class="m-0 text-sm leading-5 text-black">
          {{ t('PLANNING.SHOW.NO_CUSTOMER_INFO') }}
        </p>
      </div>
    </div>
  </EventSidebarSection>
</template>
